// btw-front/src/lib/format.js
// 입력 마스킹 — 사용자 입력 중 자동으로 하이픈을 끼워 시각적 보조.
// 모두 숫자 외 문자는 자동 제거.

/**
 * 사업자등록번호 마스킹.
 *   1248100998 → 124-81-00998
 *   12         → 12
 *   12345      → 123-45
 *   124810099  → 124-81-0099
 *
 * 입력 자리수에 따라 부분 포맷 (사용자가 타이핑 중에도 자연스럽게)
 */
export function formatBusinessNumber(input) {
  const digits = String(input || "")
    .replace(/\D/g, "")
    .slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
}

/**
 * 휴대폰 번호 마스킹 — 한국 모바일 11자리 기준.
 *   01012345678 → 010-1234-5678
 *   021234567   → 02-1234-567 (서울 2자리 prefix)
 *   0212345678  → 02-1234-5678
 *
 * 02 로 시작하면 2자리 prefix, 그 외는 3자리 prefix.
 */
export function formatPhone(input) {
  const digits = String(input || "")
    .replace(/\D/g, "")
    .slice(0, 11);

  if (digits.startsWith("02")) {
    if (digits.length <= 2) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
    // 02-XXXX-XXXX 또는 02-XXX-XXXX
    const middleLen = digits.length === 10 ? 4 : 3; // 02 + 4 + 4 = 10 (2026 기준 거의 안 씀)
    return `${digits.slice(0, 2)}-${digits.slice(2, 2 + middleLen)}-${digits.slice(2 + middleLen)}`;
  }

  if (digits.length <= 3) return digits;
  if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  if (digits.length === 10) {
    // 010 이전 구번호 (3-3-4): 010-XXX-XXXX
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  // 11자리 — 010-XXXX-XXXX
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
}

/**
 * 금액 포맷 — 천 단위 콤마. 0/빈값은 빈 문자열(placeholder 노출 가능).
 *   1000000 → "1,000,000"
 *   0       → ""
 */
export function formatAmount(value) {
  const n = Number(value) || 0;
  if (n === 0) return "";
  return n.toLocaleString("ko-KR");
}

/**
 * 금액 입력 → 정수. 숫자 외 문자 제거 후 parseInt.
 *   "1,234,567" → 1234567
 *   ""          → 0
 *   "abc"       → 0
 */
export function parseAmount(input) {
  const digits = String(input || "").replace(/\D/g, "");
  if (!digits) return 0;
  return Math.max(0, parseInt(digits, 10) || 0);
}

/**
 * 분기 표시 포맷 — DB 저장 형식은 그대로 두고 노출만 가독성 개선.
 *   "2026Q2" → "2026 Q2"
 *   ""·잘못된 입력 → ""
 *
 * DB 컬럼(po_expected_quarter CHAR(7)) 은 'yyyyQn' 그대로 유지. 본 헬퍼는 화면 노출 전용.
 */
export function formatQuarter(value) {
  if (!value || typeof value !== "string") return "";
  const m = value.match(/^(\d{4})(Q[1-4])$/);
  if (!m) return value;
  return `${m[1]} ${m[2]}`;
}

/**
 * 날짜 표시 — ISO 문자열/Date → 로컬 "YYYY-MM-DD". 해석 불가면 "".
 *   "2026-10-13T15:30:00.000Z" → "2026-10-14" (KST)
 *
 * 서버가 DATETIME 을 UTC ISO 문자열로 내려주므로 slice(0, 10) 으로 자르지 않는다
 * (UTC 날짜라 자정~09시 값이 하루 전으로 보인다). 브라우저 로컬(한국 사용자 = KST) 기준.
 */
export function formatLocalDate(value) {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * 일시 표시 — ISO 문자열/Date → 로컬 "YYYY-MM-DD HH:mm"(24시간, 분까지). 해석 불가면 "".
 *   "2026-10-14T06:30:00.000Z" → "2026-10-14 15:30" (KST)
 */
export function formatLocalDateTime(value) {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${formatLocalDate(d)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
